import {
  SiFacebook as Facebook,
  SiInstagram as Instagram,
  SiTiktok as Tiktok,
  SiWhatsapp as Whatsapp,
  SiX as X,
  SiYoutube as Youtube,
} from "@icons-pack/react-simple-icons";
import { AccessibleIcon } from "@radix-ui/react-accessible-icon";
import { Mail, MapPin, Phone, SquareArrowOutUpRight } from "lucide-react";
import { copyToClipboard, isPhoneDevice } from "@/utils/base";
import { linkPropsByDevice } from "@/utils/redirects";
import "./SocialLinks.scss";

export type SocialLinkIcon =
  | "externalLink"
  | "email"
  | "facebook"
  | "instagram"
  | "phoneForCalls"
  | "maps"
  | "tiktok"
  | "whatsapp"
  | "x"
  | "youtube";

export interface SocialLinkItem {
  href: string;
  ariaLabel: string;
  icon: SocialLinkIcon;
  copyOnDesktop?: boolean;
  copyValue?: string;
}

const SOCIAL_ICON_COMPONENTS = {
  externalLink: SquareArrowOutUpRight,
  email: Mail,
  facebook: Facebook,
  instagram: Instagram,
  phoneForCalls: Phone,
  maps: MapPin,
  tiktok: Tiktok,
  whatsapp: Whatsapp,
  x: X,
  youtube: Youtube,
} as const satisfies Record<
  SocialLinkIcon,
  React.ComponentType<{ className?: string }>
>;

interface SocialLinksProps {
  links: SocialLinkItem[];
  onLinkClick: () => void;
  className?: string;
  variant?: "primary" | "secondary";
  limit?: number;
}

/**
 * Row of icon links for social and external profiles
 */
export const SocialLinks: React.FC<SocialLinksProps> = ({
  links,
  onLinkClick,
  className,
  variant = "primary",
  limit = undefined,
}) => {
  if (links.length === 0) {
    return null;
  }

  return (
    <div
      className={`social-links${className ? ` ${className}` : ""}`}
    >
      {links.slice(0, limit).map((link, index) => {
        const IconComponent = SOCIAL_ICON_COMPONENTS[link.icon];
        const { href, ariaLabel, copyOnDesktop, copyValue } = link;

        const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
          onLinkClick();

          if (copyOnDesktop && !isPhoneDevice && copyValue) {
            event.preventDefault();
            copyToClipboard(copyValue);
          }
        };

        return (
          <a
            key={index}
            href={href}
            className="social-links__link"
            onClick={handleClick}
            {...linkPropsByDevice}
          >
            <AccessibleIcon label={ariaLabel}>
              <IconComponent className={`social-links__icon social-links__icon--${variant}`} />
            </AccessibleIcon>
          </a>
        );
      })}
    </div>
  );
};

export default SocialLinks;
